/* CompareViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./CompareViewRenderer");
const ViewMode = require("./ViewModeComponent");
const _ = require("lodash");
const jsDiff = require("diff");

class CompareViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        viewState,
        activityMonitor,
        io,
        deviceConfiguration,
        userSession,
        userService,
        storageFactory,
        MUF
    ) {
        super();
        this._logger = logger;
        this._io = io;
        this._gameEditor = gameEditor;
        this._viewState = viewState;
        this._diffString = undefined;
        this._deviceName = undefined;
        this._deviceId = undefined;
        this._deviceConfigs = [];
        this._modalUserFeedback = MUF;
        this._selectedPath = "";

        this._diffTooLarge = false;
        this._isExpandedLargeDiff = false;
        this._diffGenerationCount = 0;
        this._isDiffReady = false;

        this._deviceConfigurationEntity = deviceConfiguration;
        this._activeOrgId = userSession.activeOrganization.id;
        this._userSession = userSession;
        this._userService = userService;
        this._activityMonitor = activityMonitor;
        this._jsonDiff = false;

        this._storageFactory = storageFactory;
        this._userDataStorage = this._storageFactory.getRemoteStorageInstance(
            "view",
            "device_config_compare"
        );

        this._isJsonView = false;

        this._viewMode = new ViewMode(this._logger, this._userSession, storageFactory);

        this._value = {a: undefined, b: undefined};
        this._registerListeners();
        this._renderer = new Renderer(this);

        /**
         * wrapper to make all the calls of async function 'fn' promise chained. like fn(1);fn(2);fn(3) <=> fn(1)->fn(2)->fn(3)
         * @param {function} fn - async function to be chained
         * @param {Promise} [prevPromise=Promise.resolve()] - initial successful promise to resolve first call
         * @returns {function(...[*]): (*|Promise)}
         */

        const selfPromisedWrapper = (fn, prevPromise = Promise.resolve()) => {
            return (...args) => {
                prevPromise = prevPromise.then(() => fn(...args));
                return prevPromise;
            };
        };
        this.setValueA = selfPromisedWrapper(this.setValue.bind(this, "a"));
        this.setValueB = selfPromisedWrapper(this.setValue.bind(this, "b"));

        this.withTheVeil = this.withTheVeil([]); //init withTheVeil wrapper

        this._activityMonitor.maxInactivityPolls = this._activityMonitor.maxInactivityPolls * 3;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerDeviceFeedListeners() {
        this._device.on(
            "NamePropertyUpdated",
            (this._onNamePropUpdated = this._onNamePropUpdated.bind(this))
        );
        this._gameEditor._deviceConfigFeed.on(
            "EntityAdded",
            (this._onEntityAdded = this._onEntityAdded.bind(this))
        );
        this._gameEditor._deviceConfigFeed.on(
            "EntityRemoved",
            (this._onEntityRemoved = this._onEntityRemoved.bind(this))
        );
        this._gameEditor._deviceConfigFeed.on(
            "EntityPropertiesUpdated",
            (this._onEntityUpdated = this._onEntityUpdated.bind(this))
        );
        this._viewMode.on(
            "viewModeChanged",
            (this._onViewModeChanged = this._onViewModeChanged.bind(this))
        );
    }

    _unregisterDeviceFeedListeners() {
        this._device.removeListener("NamePropertyUpdated", this._onNamePropUpdated);
        this._gameEditor._deviceConfigFeed.removeListener("EntityAdded", this._onEntityAdded);
        this._gameEditor._deviceConfigFeed.removeListener("EntityRemoved", this._onEntityRemoved);
        this._gameEditor._deviceConfigFeed.removeListener(
            "EntityPropertiesUpdated",
            this._onEntityUpdated
        );
        this._viewMode.removeListener("viewModeChanged", this._onViewModeChanged);
    }

    _onNamePropUpdated(name) {
        this._deviceName = name;
        this._buildViewModel(this._deviceConfigCollection);
        ["a", "b"].forEach((side) => {
            let sel = _.find(this._deviceConfigs, (conf) => {
                return conf.value.id === this._value[side].id;
            });
            this._value[side] = sel.value;
        });
        this.emit("ConfigList");
    }

    _onEntityAdded(devConf) {
        this._deviceConfigCollection[devConf.id] = devConf;
        let marshalledObj = this._marshallDeviceData(devConf);
        this._deviceConfigs.push(marshalledObj);
        this.emit("ConfigList");
        if (_.isEqual(marshalledObj.id, _.get(this._value, "a.selection.id", null))) {
            if (this._deviceConfigs.length) {
                this.setValues(this._deviceConfigs[0], null);
            } else {
                this.setValue("a");
            }
        }
        if (_.isEqual(marshalledObj.id, _.get(this._value, "b.selection.id", null))) {
            if (this._deviceConfigs[0]) {
                this.setValues(null, this._deviceConfigs[0]);
            } else {
                this.setValue("b");
            }
        }
    }

    _onEntityRemoved(devConf) {
        _.unset(this._deviceConfigCollection, devConf.id);
        let marshalledObj = this._marshallDeviceData(devConf);
        let idx = this._deviceConfigs.indexOf(marshalledObj);
        this._deviceConfigs.splice(idx, 1);
        this.emit("ConfigList");
        if (_.isEqual(marshalledObj.id, this._value.a.id)) {
            if (this._deviceConfigs.length) {
                this.setValues(this._deviceConfigs[0], null);
            } else {
                this.setValue("a");
            }
        }
        if (_.isEqual(marshalledObj.id, this._value.b.id)) {
            if (this._deviceConfigs[0]) {
                this.setValues(null, this._deviceConfigs[0]);
            } else {
                this.setValue("b");
            }
        }
    }

    _onEntityUpdated(devConf) {
        this._deviceConfigCollection[devConf.id] = devConf;
        let marshalledObj = this._marshallDeviceData(devConf);

        let currentObj = this._deviceConfigs.find((obj) => {
            if (obj.value.id === devConf.id) {
                return true;
            }
        });
        let idx = this._deviceConfigs.indexOf(currentObj);
        this._deviceConfigs.splice(idx, 1, marshalledObj);

        if (marshalledObj.value.id === this._value.a.id) {
            this.setValues(marshalledObj.value, null);
        } else if (marshalledObj.value.id === this._value.b.id) {
            this.setValues(null, marshalledObj.value);
        }

        this.emit("ConfigList");
    }

    _generateJSONDiffString() {
        this._jsonDiff = true;
        let a_config = _.get(this._value, "a.capture.capture", undefined);
        let b_config = _.get(this._value, "b.capture.capture", undefined);
        this.emit("Diff", a_config, b_config);
    }

    async _generateDiffString() {
        // let a_config = _.get(this._value, "a.config.capture", undefined);
        // let b_config = _.get(this._value, "b.config.capture", undefined);
        this._diffGenerationCount++;
        let a_config = this._confA || " ";
        let b_config = this._confB || " ";
        if (a_config && b_config) {
            if (_.isObject(this._confA) || _.isObject(this._confB)) {
                this._confA = _.get(
                    this._value,
                    "a.capture.capture." + this._selectedPath + ".responseStr",
                    " "
                );
                this._confB = _.get(
                    this._value,
                    "b.capture.capture." + this._selectedPath + ".responseStr",
                    " "
                );

                a_config = this._confA || " ";
                b_config = this._confB || " ";
            }

            await this._getTextDiff(a_config, b_config, this._selectedPath);
        } else {
            this._diffString = undefined;
        }

        this.emit("Diff");
        return Promise.resolve();
    }

    async _getTextDiff(a_config = "", b_config = "", path = "") {
        if (a_config.split("\n").length > 5000 || b_config.split("\n").length > 5000) {
            const workInput = {
                oldDeviceConfigId: _.get(this._value, "a.id"),
                newDeviceConfigId: _.get(this._value, "b.id"),
                path: path,
                jsonDiff: false,
                compactViewMode: this.viewMode.compactView,
            };

            const workResults = await this._deviceConfigurationEntity.runConfigDiffWork(
                this._activeOrgId,
                {},
                workInput,
                {}
            );
            this._diffString = _.get(workResults.summary, "diffStr", "");
        } else {
            let context = this.viewMode.compactView === true ? 2 : this._getMax(a_config, b_config);

            let diffStr = jsDiff.createTwoFilesPatch(null, null, a_config, b_config, null, null, {
                context: context,
            });
            this._diffString = diffStr;
        }
        return Promise.resolve();
    }

    _getMax(a, b) {
        let a_len = a.match(/.*\r?\n|.+$/g).length;
        let b_len = b.match(/.*\r?\n|.+$/g).length;
        return Math.max(a_len, b_len);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get id() {
        return this._deviceId;
    }

    get io() {
        return this._io;
    }

    get deviceName() {
        return this.getDeviceName();
    }

    getDeviceName() {
        return this._deviceName;
    }

    get diffString() {
        return this.getDiffString();
    }

    getDiffString() {
        return this._diffString;
    }

    get contextLineCount() {
        return this.getContextLineCount();
    }

    get userSession() {
        return this._userSession;
    }

    get userService() {
        return this._userService;
    }

    get viewMode() {
        return this._viewMode;
    }

    /**
     * Set compared values, and show loader
     * @param {Object} firstEntity
     * @param {Object} secondEntity
     * @param {boolean} emit - if we need to emit event
     */

    setValues(firstEntity, secondEntity, emit = true) {
        this._isDiffReady = false;
        const setValuesProm = Promise.all([
            firstEntity && this.setValueA(firstEntity, emit),
            secondEntity && this.setValueB(secondEntity, emit),
        ]);

        return this.withTheVeil(setValuesProm).catch(
            console.warn.bind(console, "withTheVeil error:")
        );
    }

    /**
     * function wrapper to show Veil and keep it shown until all promises are resolved (and newly added promises as well)
     * @param {Array} [promiseSet=[]] - initial empty array
     * @returns {function(*=): (*|Promise)}
     */

    withTheVeil(promiseSet = []) {
        const showVeil = () =>
            this._modalUserFeedback.displayLoading({message: "Loading comparison"});
        const hideVeil = () => {
            if (!this._isDiffReady) {
                return Promise.resolve();
            }
            return this._modalUserFeedback.hideDisplayed();
        };

        const getPromiseSet = () => promiseSet;
        const addToSet = (prom) => (promiseSet = promiseSet.concat(prom));
        const resetSet = () => (promiseSet = []);

        return (prom) => {
            let promiseSet = getPromiseSet();
            if (promiseSet.length === 0) {
                promiseSet = addToSet(showVeil());
            }
            promiseSet = addToSet(prom);
            return Promise.all(promiseSet).then(() => {
                if (promiseSet === getPromiseSet()) {
                    resetSet();
                    return hideVeil().then(() => ({done: true}));
                }
                return Promise.resolve({done: false});
            });
        };
    }

    loadRawConfiguration(config) {
        if (!this._isJson) {
            this._isJson = {};
        }
        return config
            .loadRelated(
                "rawConfig",
                "RawConfig",
                false,
                {},
                {config: true, capture: true, configObj: true, isConfigObj: true}
            )
            .then((rawConfigEntities) => {
                if (rawConfigEntities.length) {
                    return rawConfigEntities[0];
                }
            });
    }

    async setValue(side, val, emit = true) {
        this._value[side] = val;
        if (_.isUndefined(this._value[side].capture)) {
            let configEntity = this._deviceConfigCollection[val.id];
            if (configEntity) {
                // Request device rawConfiguration from the server if we don't have it yet
                const rawConfig = await this.loadRawConfiguration(configEntity, side);
                this._value[side].capture = rawConfig;
                if (!this._jsonDiff) {
                    await this._generateDiffString();
                } else {
                    this._generateJSONDiffString();
                }
                if (emit) {
                    this.emit("Value", side, this._value[side]);
                }

                return Promise.resolve();
            }
        }
        if (!this._jsonDiff) {
            await this._generateDiffString();
        } else {
            this._generateJSONDiffString();
        }
        if (emit) {
            this.emit("Value", side, this._value[side]);
        }
        return Promise.resolve();
    }

    getValue(side) {
        return this._value[side];
    }

    getOptions() {
        return this._deviceConfigs;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    whenModelIsReady() {
        this._viewType = this._userDataStorage.getData("viewType", true);
        if (this._viewType === "json") {
            this._isJsonView = true;
        }
        return this.viewMode
            .whenStorageIsReady()
            .then(() => this._gameEditor.whenCommandSetsAreReady())
            .then(() => this._gameEditor.whenDeviceIsReady())
            .then(() => {
                this._device = this._gameEditor.getDevice();
                this._deviceName = this._device.name;
                this._deviceId = this._device.id;
                return this._gameEditor.whenDeviceConfigIsReady();
            })
            .then(() => {
                this.selectedConfId = _.get(this._viewState, "selectedConfigId", undefined);
                this._gameEditor.emit("ClearViewState");

                return Promise.resolve();
            })
            .then(() => {
                this.isJSONConf = false;
                if (this._device.controlType && this._device.controlType !== "NA") {
                    this.isJSONConf = true;
                }
                let filter = {};
                if (
                    _.get(this._gameEditor._solutionManager, "_activeSolution.name") ===
                    "config_drift"
                ) {
                    filter = !_.isEmpty(this._device)
                        ? {$and: [{deviceId: this._device.getId()}, {fromCapture: true}]}
                        : {};
                } else {
                    filter = !_.isEmpty(this._device) ? {deviceId: this._device.getId()} : {};
                }

                return this._deviceConfigurationEntity.loadDeviceConfigurations(
                    this._gameEditor._activeOrgId,
                    filter,
                    {},
                    {_id: -1},
                    {
                        skip: 0,
                        limit: 2000,
                    }
                );
                // return this._device.loadRelatedDeviceConfigurations({}, {}, {createdOn: -1}, {
                //     "skip": 0,
                //     "limit": 2000
                // });
            })
            .then((configList) => {
                this._deviceConfigList = configList;
                this._registerDeviceFeedListeners();
                this._deviceConfigCollection = {};
                this._deviceConfigList.forEach((i) =>
                    Object.assign(this._deviceConfigCollection, {[i.getId()]: i})
                );
                return this._buildViewModel(this._deviceConfigCollection);
            })
            .catch((err) => {
                this._logger.warn({error: err}, "CompareViewComponent.whenModelIsReady() error.");
            });
    }

    shutdown() {
        if (!_.isUndefined(this._gameEditor._deviceConfigFeed)) {
            this._unregisterDeviceFeedListeners();
        }
        this._unregisterListeners();
        //reset session timeout to normal
        this._activityMonitor.maxInactivityPolls =
            this._activityMonitor._options.maxInactivityPolls;

        let promises = [this._viewMode.shutdown(), this._renderer.shutdown()];
        return Promise.all(promises).then(
            () => {
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._io = undefined;
                this._gameEditor = undefined;
                this._viewMode = undefined;
                this._deviceConfigList = undefined;
                this._diffString = undefined;
                this._deviceName = undefined;
                this._deviceId = undefined;
                this._selectedPath = undefined;
                this._deviceConfigs = undefined;
                this._value = undefined;
                this._activityMonitor = undefined;
                this._isExpandedLargeDiff = undefined;
                this._diffGenerationCount = undefined;
                this._isDiffReady = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "CompareViewComponent.shutdown() error.");
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onViewModeChanged() {
        this.withTheVeil([this._generateDiffString()]);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _buildViewModel(cache) {
        this._deviceConfigs = [];
        Object.keys(cache).forEach((id) => {
            let marshalledObj = this._marshallDeviceData(cache[id]);
            this._deviceConfigs.push(marshalledObj);
            let selectedConfId = this.selectedConfId;
            if (selectedConfId && selectedConfId === id) {
                this._value.b = marshalledObj.value;
            }
            if (marshalledObj.value.isStable) {
                this._stableConf = marshalledObj;
                if (selectedConfId !== id) {
                    this._value.a = marshalledObj.value;
                }
            }
        });

        if (!_.isEmpty(this._deviceConfigs)) {
            this._sortDeviceViewModel();
        } else {
            this.renderer._showDefaultMessageDisplay();
            return;
        }
        if (_.isEmpty(this._value.b) && this._deviceConfigs.length) {
            this._value.b = this._deviceConfigs[0].value;
        }
        if (_.isEmpty(this._value.a) && this._deviceConfigs.length) {
            this._value.a = this._deviceConfigs[0].value;
        }

        return this.setValues(this._value.a, this._value.b, false);
    }

    _marshallDeviceData(devConf) {
        let timestamp = devConf.startTime
            ? devConf.startTime
            : devConf.capturedTime
            ? devConf.capturedTime
            : devConf.updatedOn;
        timestamp = new Date(timestamp);

        let localDateTime = "Unknown";
        if (_.isDate(timestamp)) {
            localDateTime = timestamp.toLocaleDateString() + " " + timestamp.toLocaleTimeString();
        }

        let label, name;

        if (devConf.description) {
            label = devConf.description + " - " + localDateTime;
            name = devConf.description + " - " + localDateTime;
        } else {
            label = localDateTime;
            name = localDateTime;
        }

        label +=
            (devConf.initiatorUser ? " (" + devConf.initiatorUser : " (") +
            (devConf.initiatorIp ? " from " + devConf.initiatorIp + ")" : ")");
        name +=
            (devConf.initiatorUser ? " (" + devConf.initiatorUser : " (") +
            (devConf.initiatorIp ? " from " + devConf.initiatorIp + ")" : ")");

        if (!devConf.initiatorUser && !devConf.initiatorIp) {
            label = label.slice(0, -3);
            name = name.slice(0, -3);
        }

        let marshalledObj = {
            label: label,
            value: {
                id: devConf.id,
                name: name,
                isStable: devConf.isStableDeviceConfiguration,
                wasDifferentAtCaptureTime: devConf.wasDifferentAtCaptureTime,
                timestamp: timestamp ? timestamp : "Unknown",
            },
        };

        return marshalledObj;
    }

    _sortDeviceViewModel() {
        this._deviceConfigs = this._deviceConfigs.sort((a, b) => {
            let atime = a.value.timestamp;
            let btime = b.value.timestamp;
            if (atime < btime) {
                return 1;
            } else if (atime > btime) {
                return -1;
            } else {
                return 0;
            }
        });
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getViewModel() {
        return this._deviceConfigs;
    }

    makeDefaultSnapshot(id) {
        return this._deviceConfigCollection[id].runSetAsStableConfigWork();
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = CompareViewComponent;

CompareViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.activityMonitor",
    "$teve.io",
    "$teve.entities.DeviceConfiguration",
    "$teve.userSession",
    "$teve.services.User",
    "$teve.storageFactory",
    "$teve.ui.userFeedback",
];

CompareViewComponent.$displayDetails = {
    type: "View",
    name: "Comparison",
    icon: "configdrift.compare",
    title: "Comparisons",
    shown: true,
    order: 8,
};
